"use client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@workspace/ui/components/accordion";
import { Checkbox } from "@workspace/ui/components/checkbox";
import { Slider } from "@workspace/ui/components/custom-slider";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@workspace/ui/components/input-group";
import { Label } from "@workspace/ui/components/label";
import { cn } from "@workspace/ui/lib/utils";
import { Search } from "lucide-react";
import { useQueryStates } from "nuqs";
import { useRef, useState } from "react";
import type { DateRange } from "react-day-picker";
import { DatePickerWithRange } from "@/components/data-table/date-picker-with-range";
import { useDebouncedCallback } from "@/lib/hooks/use-debounce";
import {
  type AiFilterResult,
  MarketplaceAiFilterInput,
} from "./marketplace-ai-filter-input";
import { marketplaceFilterFields } from "./marketplace-filter-constants";
import { marketplaceSearchParamsParser } from "./marketplace-search-params";
import { useAiFilterMap } from "./use-ai-filter-map";

export function MarketplaceFilterControls({
  userLatitude,
  userLongitude,
}: {
  userLatitude?: number;
  userLongitude?: number;
}) {
  const [searchParams, setSearchParams] = useQueryStates(
    marketplaceSearchParamsParser
  );
  const { moveMap, handleNearMe } = useAiFilterMap();
  const filterFields = marketplaceFilterFields;

  const handleSliderChange = (field: string, value: number[]) => {
    if (field === "compensation") {
      setSearchParams({
        ...searchParams,
        compensation: value,
      });
    }
  };

  const handleCheckboxChange = (field: string, values: string[]) => {
    setSearchParams({
      ...searchParams,
      [field]: values,
    });
  };

  const handleDateRangeChange = (dateRange: DateRange | undefined) => {
    if (dateRange?.from && dateRange?.to) {
      setSearchParams({
        ...searchParams,
        posted_date: [dateRange.from, dateRange.to],
      });
    } else {
      setSearchParams({
        ...searchParams,
        posted_date: null,
      });
    }
  };

  const resetField = (field: string) => {
    const resetValues: Record<string, unknown> = { ...searchParams };

    switch (field) {
      case "compensation": {
        resetValues.compensation = null;
        break;
      }
      case "compensation_rate": {
        resetValues.compensation_rate = null;
        break;
      }
      case "category": {
        resetValues.category = null;
        break;
      }
      case "work_model": {
        resetValues.work_model = null;
        break;
      }
      case "commitment": {
        resetValues.commitment = null;
        break;
      }
      case "priority": {
        resetValues.priority = null;
        break;
      }
      case "resume": {
        resetValues.resume = null;
        break;
      }
      case "posted_date": {
        resetValues.posted_date = null;
        break;
      }
      default: {
        resetValues[field] = null;
        break;
      }
    }

    setSearchParams(resetValues);
  };

  const getFieldValue = (field: string): unknown => {
    switch (field) {
      case "compensation": {
        // Return default range when filters are null, otherwise return current values
        if (
          !searchParams.compensation ||
          searchParams.compensation.length === 0
        ) {
          return [0, 10_000]; // Default range
        }
        return searchParams.compensation;
      }
      case "compensation_rate":
        return searchParams.compensation_rate || [];
      case "category":
        return searchParams.category || [];
      case "work_model":
        return searchParams.work_model || [];
      case "commitment":
        return searchParams.commitment || [];
      case "priority":
        return searchParams.priority || [];
      case "resume":
        return searchParams.resume || [];
      case "posted_date":
        return searchParams.posted_date?.length === 2
          ? {
              from: searchParams.posted_date[0],
              to: searchParams.posted_date[1],
            }
          : undefined;
      default:
        return [];
    }
  };

  const handleAiFiltersGenerated = (result: AiFilterResult) => {
    setSearchParams({
      ...searchParams,
      ...result.filters,
    });

    moveMap(result.map);
    handleNearMe(result.location);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="font-medium text-sm">Natural language filters</Label>
        <MarketplaceAiFilterInput
          onFiltersGenerated={handleAiFiltersGenerated}
          userLatitude={userLatitude}
          userLongitude={userLongitude}
        />
      </div>

      <Accordion
        defaultValue={filterFields?.flatMap(({ defaultOpen, value }) =>
          defaultOpen ? [value as string] : []
        )}
        type="multiple"
      >
        {filterFields?.map((field) => {
          const value = field.value as string;
          const fieldValue = getFieldValue(value);

          // Special handling for compensation slider
          let hasValue = false;
          if (value === "compensation") {
            hasValue =
              searchParams.compensation !== null &&
              searchParams.compensation.length > 0;
          } else if (value === "posted_date") {
            hasValue =
              searchParams.posted_date !== null &&
              searchParams.posted_date.length === 2;
          } else {
            const paramValue = searchParams[value as keyof typeof searchParams];
            hasValue = paramValue !== null && paramValue !== undefined;
            if (hasValue && Array.isArray(paramValue)) {
              hasValue = paramValue.length > 0;
            }
          }

          return (
            <AccordionItem className="border-none" key={value} value={value}>
              <AccordionTrigger className="w-full px-2 py-2 hover:no-underline data-[state=closed]:text-muted-foreground data-[state=open]:text-foreground hover:data-[state=closed]:text-foreground focus-within:data-[state=closed]:text-foreground">
                <div className="flex w-full items-center justify-between gap-2 truncate">
                  <div className="flex items-center gap-2 truncate">
                    <p className="font-medium text-sm">{field.label}</p>
                  </div>
                  {hasValue && (
                    <button
                      className="text-muted-foreground text-xs hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        resetField(value);
                      }}
                      type="button"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                {/* REMINDER: avoid the focus state to be cut due to overflow-hidden */}
                {/* REMINDER: need to move within here because of accordion height animation */}
                <div className="p-1">
                  {(() => {
                    switch (field.type) {
                      case "checkbox": {
                        return (
                          <CheckboxFilter
                            field={field}
                            onChange={(values) =>
                              handleCheckboxChange(value, values)
                            }
                            value={fieldValue as string[]}
                          />
                        );
                      }
                      case "slider": {
                        return (
                          <SliderFilter
                            field={field}
                            onChange={(values) =>
                              handleSliderChange(value, values)
                            }
                            value={fieldValue as number[]}
                          />
                        );
                      }
                      case "input": {
                        return (
                          <InputFilter
                            field={field}
                            onChange={(inputValue) => {
                              setSearchParams({
                                ...searchParams,
                                [field.value as string]: inputValue,
                              });
                            }}
                            value={fieldValue as string}
                          />
                        );
                      }
                      case "timerange": {
                        return (
                          <DateRangeFilter
                            onChange={handleDateRangeChange}
                            value={fieldValue as DateRange | undefined}
                          />
                        );
                      }
                      default:
                        return null;
                    }
                  })()}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}

// Checkbox Filter Component (similar to DataTableFilterCheckbox)
export function CheckboxFilter({
  field,
  value,
  onChange,
}: {
  field: { options?: Array<{ label: string; value: unknown }>; value: unknown };
  value: string[];
  onChange: (values: string[]) => void;
}) {
  const [inputValue, setInputValue] = useState("");
  const options = field.options || [];

  const filterOptions = options.filter(
    (option: { label: string; value: unknown }) =>
      inputValue === "" ||
      option.label.toLowerCase().includes(inputValue.toLowerCase())
  );

  const handleCheckboxChange = (optionValue: string, checked: boolean) => {
    const newValue = checked
      ? [...(value || []), optionValue]
      : (value || []).filter((v: string) => v !== optionValue);
    onChange(newValue);
  };

  return (
    <div className="grid gap-2">
      {options.length > 4 && (
        <InputGroup>
          <InputGroupInput
            aria-label="Search options"
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect="off"
            className="h-9 rounded-lg"
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Search"
            spellCheck="false"
            value={inputValue}
          />
          <InputGroupAddon>
            <Search className="mt-0.5 h-4 w-4" />
          </InputGroupAddon>
        </InputGroup>
      )}
      <div className="max-h-48 overflow-y-auto rounded-lg border border-border empty:border-none">
        {filterOptions.map(
          (option: { label: string; value: unknown }, index: number) => {
            const checked = (value || []).includes(option.value as string);

            return (
              <div
                className={cn(
                  "group relative flex items-center gap-2 px-2 py-2 hover:bg-accent/50",
                  index !== filterOptions.length - 1 ? "border-b" : undefined
                )}
                key={String(option.value)}
              >
                <Checkbox
                  checked={checked}
                  id={`${field.value as string}-${option.value as string}`}
                  onCheckedChange={(isChecked) => {
                    handleCheckboxChange(
                      option.value as string,
                      isChecked as boolean
                    );
                  }}
                />
                <Label
                  className="flex w-full items-center justify-center gap-1 truncate text-foreground/70 group-hover:text-accent-foreground"
                  htmlFor={`${field.value as string}-${option.value as string}`}
                >
                  <span className="truncate font-normal">{option.label}</span>
                </Label>
              </div>
            );
          }
        )}
      </div>
    </div>
  );
}

// Slider Filter Component (similar to DataTableFilterSlider)
export function SliderFilter({
  field,
  value,
  onChange,
}: {
  field: {
    min: number;
    max: number;
    value: unknown;
    trailing?: string;
    leading?: string;
  };
  value: number[];
  onChange: (values: number[]) => void;
}) {
  // Ensure we always have valid values, fallback to field defaults
  const validValue =
    value?.length === 2 && value.every((v) => v !== null && v !== undefined)
      ? value
      : [field.min, field.max];

  const [input, setInput] = useState<number[]>(validValue);
  const debouncedOnChange = useDebouncedCallback(onChange, 500);

  // Re-sync the local copy when the prop changes (e.g. an external reset),
  // computed during render rather than in an effect.
  const prevValueRef = useRef(value);
  if (value !== prevValueRef.current) {
    prevValueRef.current = value;
    setInput(validValue);
  }

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    const next: number[] = [newValue, input?.[1] ?? field.max];
    setInput(next);
    debouncedOnChange(next);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    const next: number[] = [input?.[0] ?? field.min, newValue];
    setInput(next);
    debouncedOnChange(next);
  };

  const handleSliderChange = (values: number[]) => {
    setInput(values);
    debouncedOnChange(values);
  };

  return (
    <div className="grid gap-2">
      <div className="flex items-center gap-4">
        <div className="grid w-full gap-1.5">
          <Label
            className="px-2 text-muted-foreground"
            htmlFor={`min-${field.value}`}
          >
            Min.
          </Label>
          <InputGroup>
            <InputGroupInput
              autoCapitalize="none"
              autoComplete="off"
              autoCorrect="off"
              className="h-9 rounded-lg"
              id={`min-${field.value}`}
              max={field.max}
              min={field.min}
              name={`min-${field.value}`}
              onChange={handleMinChange}
              placeholder="from"
              spellCheck="false"
              type="number"
              value={input[0]}
            />
            <InputGroupAddon>{field.leading}</InputGroupAddon>
            <InputGroupAddon align="inline-end">
              {field.trailing}
            </InputGroupAddon>
          </InputGroup>
        </div>
        <div className="grid w-full gap-1.5">
          <Label
            className="px-2 text-muted-foreground"
            htmlFor={`max-${field.value}`}
          >
            Max.
          </Label>
          <InputGroup>
            <InputGroupInput
              autoCapitalize="none"
              autoComplete="off"
              autoCorrect="off"
              className="h-9 rounded-lg"
              id={`max-${field.value}`}
              max={field.max}
              min={field.min}
              name={`max-${field.value}`}
              onChange={handleMaxChange}
              placeholder="to"
              spellCheck="false"
              type="number"
              value={input[1]}
            />
            <InputGroupAddon>{field.leading}</InputGroupAddon>
            <InputGroupAddon align="inline-end">
              {field.trailing}
            </InputGroupAddon>
          </InputGroup>
        </div>
      </div>
      <Slider
        max={field.max}
        min={field.min}
        onValueChange={handleSliderChange}
        value={input}
      />
    </div>
  );
}

// Input Filter Component (similar to DataTableFilterInput)
export function InputFilter({
  field,
  value,
  onChange,
}: {
  field: { value: unknown };
  value: string;
  onChange: (value: string) => void;
}) {
  const [input, setInput] = useState<string>(value || "");
  const debouncedOnChange = useDebouncedCallback(onChange, 300);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInput(newValue);
    debouncedOnChange(newValue.trim() === "" ? "" : newValue);
  };

  return (
    <div className="grid w-full gap-1.5">
      <Label
        className="sr-only px-2 text-muted-foreground"
        htmlFor={field.value as string}
      >
        {field.value as string}
      </Label>
      <InputGroup>
        <InputGroupInput
          autoCapitalize="none"
          autoComplete="off"
          autoCorrect="off"
          className="h-9 rounded-lg"
          id={field.value as string}
          name={field.value as string}
          onChange={handleChange}
          placeholder="Search"
          spellCheck="false"
          value={input}
        />
        <InputGroupAddon>
          <Search className="mt-0.5 h-4 w-4" />
        </InputGroupAddon>
      </InputGroup>
    </div>
  );
}

// Date Range Filter Component (similar to DataTableFilterTimerange)
export function DateRangeFilter({
  value,
  onChange,
}: {
  value: DateRange | undefined;
  onChange: (date: DateRange | undefined) => void;
}) {
  return <DatePickerWithRange date={value} setDate={onChange} />;
}
